import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ShieldCheck } from 'lucide-react'
import clsx from 'clsx'

/**
 * Public verification page (bet #4): no auth, shareable link, no personal data.
 * S-UIX-14: links the downloadable PDF certificate alongside the SVG badge.
 */

interface PublicVerify {
  legalName: string
  registryNumber: string | null
  countryCode: string | null
  status: string | null
  verdict: string
  sources: number
  crossVerified: boolean
  checkedAt: string
}

export default function PublicVerifyPage() {
  const { recordId } = useParams()
  const { t } = useTranslation()
  const [result, setResult] = useState<PublicVerify | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    fetch(`/v1/public/verify/${recordId}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('not found'))))
      .then(setResult)
      .catch(() => setFailed(true))
  }, [recordId])

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#f8fafc] p-6">
      <div className="w-full max-w-lg rounded-2xl border border-slate-200 bg-white p-8 shadow-sm text-center">
        <ShieldCheck className={clsx('mx-auto h-12 w-12', result?.crossVerified ? 'text-emerald-500' : 'text-slate-400')} aria-hidden />
        <h1 className="mt-4 text-2xl font-bold">{result?.legalName ?? (failed ? t('public.notFound', 'Record not found') : '…')}</h1>
        {result && (
          <>
            <div
              className={clsx(
                'mt-3 inline-flex rounded-full px-4 py-1.5 text-sm font-bold',
                result.crossVerified ? 'bg-emerald-500/10 text-emerald-600' : 'bg-slate-500/10 text-slate-600',
              )}
            >
              {result.verdict}
            </div>
            <dl className="mt-6 space-y-2 text-left text-sm">
              <Row label={t('public.registryNumber', 'Registry number')} value={result.registryNumber} />
              <Row label={t('public.country', 'Country')} value={result.countryCode} />
              <Row label={t('public.status', 'Status')} value={result.status} />
              <Row label={t('public.sources', 'Independent sources')} value={String(result.sources)} />
              <Row label={t('public.checkedAt', 'Checked at')} value={result.checkedAt.slice(0, 16).replace('T', ' ')} />
            </dl>
            <p className="mt-6 text-xs text-slate-500">
              {t('public.footer', 'Verified against official government registries. Every fact is traceable to its source.')} © VBAnnagate — vbannagate.com
            </p>
            <div className="mt-4 flex items-center justify-center gap-4 text-xs">
              <a href={`/v1/public/badge/${recordId}.svg`} className="text-brand-600 hover:underline">
                {t('public.badge', 'Download embeddable badge (SVG)')}
              </a>
              <a href={`/v1/public/certificate/${recordId}.pdf`} className="text-brand-600 hover:underline">
                {t('public.certificate', 'Verification certificate (PDF)')}
              </a>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

function Row({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-slate-500">{label}</dt>
      <dd className="font-medium">{value ?? '—'}</dd>
    </div>
  )
}
